import { useState } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useTableSort } from '../../lib/table-sort';
import { useInvestigationBillPatientBills } from './investigation-bill.queries';
import { currency, formatInvestigationDateTime } from './investigation-bill.utils';
import type { InvestigationBill } from './types';

export const InvestigationBillAllBillList = ({ row }: { row: InvestigationBill }) => {
  const uhid = row.patientSnapshot.uhid;
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const query = useInvestigationBillPatientBills(uhid, sortBy, sortOrder);
  const bills = query.data?.data ?? [];
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their billing history can't be looked up." />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Unable to load bills" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;

  return (
    <>
      <header className="investigation-bill-detail-banner">
        <div className="investigation-bill-detail-banner-row"><span>All Bill — {row.patientSnapshot.name}</span></div>
        <p>UHID {uhid} · {bills.length} bill{bills.length === 1 ? '' : 's'}</p>
      </header>
      <section className="investigation-bill-detail-subsection">
        {bills.length === 0 ? (
          <p className="investigation-bill-detail-empty-note">No Investigation bills were found for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="investigation-bill-detail-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billId">Bill No</SortableTh><SortableTh control={tableSort} column="billDate">Date</SortableTh><SortableTh control={tableSort} column="doctor">Doctor</SortableTh><SortableTh control={tableSort} column="total">Total</SortableTh><SortableTh control={tableSort} column="discount">Discount</SortableTh><SortableTh control={tableSort} column="grandTotal">Grand Total</SortableTh><SortableTh control={tableSort} column="paid">Paid</SortableTh><SortableTh control={tableSort} column="due">Due</SortableTh><SortableTh control={tableSort} column="status">Status</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {bills.map((bill, index) => (
                  <tr key={bill.id} className={bill.id === row.id ? 'investigation-bill-current-row' : undefined}>
                    <td>{index + 1}</td>
                    <td>{bill.billing.billNumber}</td>
                    <td>{formatInvestigationDateTime(bill.billDate)}</td>
                    <td>{doctorNameOrDash(bill.referringDoctor?.name)}</td>
                    <td>{currency(bill.billing.total)}</td>
                    <td>{currency(bill.billing.totalDiscount)}</td>
                    <td>{currency(bill.billing.grandTotal)}</td>
                    <td>{currency(bill.billing.totalPayment)}</td>
                    <td>{currency(bill.billing.dueAmount)}</td>
                    <td><span className={`investigation-bill-badge ${bill.billing.billStatus}`}>{bill.billing.billStatus === 'due' ? 'Due' : bill.billing.billStatus === 'cancelled' ? 'Cancelled' : 'Paid'}</span></td>
                    <td><Link className="investigation-bill-row-action" to={`/investigation-bill/${bill.id}`}>{bill.id === row.id ? 'Current' : 'View'}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};
